import React, {useContext, useEffect, useState} from 'react';
import { Modal } from 'react-bootstrap';
import axios from 'axios';
import {RecordsManagementContext} from "../StatesMangement/InitialiseContext";
import {Elink, PayNowForStorage} from "../CommonComponents/Routes";

// Error boundary to catch unexpected render crashes that could blank the screen
class PayNowErrorBoundary extends React.Component {
    constructor(props){
        super(props); this.state = { hasError:false, error:null };
    }
    static getDerivedStateFromError(error){ return { hasError:true, error }; }
    componentDidCatch(error, info){ console.error('[StoragePayNowModal] render error', error, info); }
    render(){
        if (this.state.hasError){
            return <div style={{padding:12,fontSize:12,color:'red'}}>Failed to load Pay Now modal. Please refresh the page.</div>;
        }
        return this.props.children;
    }
}

const StoragePayNowModal = ({
                                show,
                                onHide,
                                monthlyFee = 0,
                                lastBalance = 0,
                                currency = '$',
                                onPaid
                            }) => {
    // Safe context consumption inside component to avoid React hook error.
    const ctx = useContext(RecordsManagementContext) || {};
    const SP_PageDetails = ctx.SP_PageDetails || {};
    const Header = SP_PageDetails.Header || {};
    const [autoPay, setAutoPay] = useState(true);
    const [mode, setMode] = useState('monthly_dues'); // 'monthly_dues' | 'custom'
    const [customAmount, setCustomAmount] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    // Reset when modal opens
    useEffect(() => {
        if (show) {
            setMode('monthly_dues');
            setAutoPay(true);
            setCustomAmount('');
            setError('');
            setSubmitting(false);
            console.log('[StoragePayNowModal] opened');
        }
    }, [show]);

    // Seed custom amount when switching to custom
    useEffect(() => {
        if (!show) return;
        if (mode === 'custom') {
            const seedRaw = parseFloat(lastBalance) || parseFloat(monthlyFee) || 0;
            if (seedRaw > 0) {
                const seed = seedRaw.toFixed(2);
                setCustomAmount(seed);
                console.log('[StoragePayNowModal] seed customAmount', seed);
            } else {
                setCustomAmount('');
            }
        } else if (mode === 'monthly_dues') {
            setCustomAmount('');
        }
    }, [mode, lastBalance, monthlyFee, show]);

    // Validate custom amount ONLY via effect to avoid setState inside render logic
    useEffect(() => {
        if (mode !== 'custom') { setError(''); return; }
        const val = customAmount.trim();
        if (val === '') { setError('Enter an amount.'); return; }
        if (!/^\d+(\.\d{0,2})?$/.test(val)) { setError('Use numbers only (max 2 decimals).'); return; }
        const num = parseFloat(val);
        if (!Number.isFinite(num) || num <= 0) { setError('Amount must be greater than 0.'); return; }
        setError('');
    }, [customAmount, mode]);

    const onChangeCustom = (e) => {
        let val = e.target.value;
        val = val.replace(/[^0-9.]/g,'');
        const firstDot = val.indexOf('.');
        if (firstDot !== -1){
            val = val.slice(0, firstDot + 1) + val.slice(firstDot + 1).replace(/\./g,'');
        }
        val = val.replace(/^(\d+)(\.\d{0,2}).*$/, '$1$2');
        setCustomAmount(val);
    };

    // Derived booleans
    const hasGateway = !!Header.PaymentGateway;
    const hasValidCard = !!Header.ValidCreditCard; // if available
    const isCustomValid = mode === 'custom' ? error === '' && customAmount.trim() !== '' : true;

    const canSubmit = hasGateway && hasValidCard && !submitting && isCustomValid;

    const safeMonthlyFee = Number.isFinite(parseFloat(monthlyFee)) ? parseFloat(monthlyFee).toFixed(2) : '0.00';

    const submit = async () => {
        if (!canSubmit) return;
        if (!window.confirm('Are You Sure?')) return;
        setSubmitting(true);
        try {
            const paymentMode = mode === 'custom' ? 'custom' : 'monthly_dues';
            const amount = paymentMode === 'custom' ? customAmount : safeMonthlyFee;
            console.log('[StoragePayNowModal] submit', { paymentMode, amount });
            const resp = await axios.post(PayNowForStorage, {
                Storage_key: Elink,
                auto_pay: autoPay,
                payment_mode: paymentMode,
                storage_amount: amount
            });
            const message = resp.data && resp.data.message ? resp.data.message : 'Payment processed.';
            alert(message);
            if (onPaid) onPaid(message, paymentMode); // pass paymentMode as second param
            onHide();
            // Removed reload to prevent blank flash
        } catch (e) {
            console.error('[StoragePayNowModal] payment error', e);
            alert('Payment failed. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <PayNowErrorBoundary>
            <Modal show={show} onHide={onHide} centered backdrop="static" className="storage-pay-now-modal">
                <Modal.Header>
                    <h5 style={{ color: '#37abdb', fontSize: '15px', fontWeight: 600 }}>Storage Payment</h5>
                    <button onClick={onHide} style={{ color: 'red', background: 'white', border: 'white' }}>
                        <b className="contactInfoCloseBtn">X</b>
                    </button>
                </Modal.Header>
                <Modal.Body>
                    <div className="dialog-box-container dialog-box-content">
                        {/* Auto Pay */}
                        <div className="form-group row mb-3">
                            <div className="form-check">
                                <label className="form-check-label" htmlFor="auto_pay">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id="auto_pay"
                                        checked={autoPay}
                                        onChange={(e)=> setAutoPay(e.target.checked)}
                                    />{' '}Auto Pay with this card each billing cycle.
                                </label>
                            </div>
                        </div>
                        {/* Payment Option Radios */}
                        <div className="justify-content-center text-center align-items-center mb-2" id="options_modal">
                            <label className="form-check-label" htmlFor="total_dues_option" style={{ marginRight: 50 }}>
                                <input
                                    className="form-check-input pay_options"
                                    type="radio"
                                    name="inlineRadioOptions"
                                    id="total_dues_option"
                                    value="total_dues_option"
                                    checked={mode === 'monthly_dues'}
                                    onChange={()=> setMode('monthly_dues')}
                                />{' '}Monthly Dues ({currency}{safeMonthlyFee})
                            </label>
                            <label className="form-check-label" htmlFor="other_amount_option" style={{ marginLeft: 20 }}>
                                <input
                                    className="form-check-input pay_options"
                                    type="radio"
                                    name="inlineRadioOptions"
                                    id="other_amount_option"
                                    value="other_amount_option"
                                    checked={mode === 'custom'}
                                    onChange={()=> setMode('custom')}
                                />{' '}Custom Amount
                            </label><br />
                        </div>
                        {/* Custom Amount Field */}
                        {mode === 'custom' && (
                            <div className="justify-content-center text-center align-items-center mb-2" id="cus_text_field" style={{ width: '150px' }}>
                                <span id="custom_amount_label" style={{ position: 'absolute', top: 5, left: 15, color: 'darkgray' }}>{currency}</span>
                                <input
                                    style={{ width: '120px', height: '32px', display: 'block', textAlign: 'center' }}
                                    type="text"
                                    name="custom_amount"
                                    placeholder="0.00"
                                    id="custom_amount"
                                    value={customAmount}
                                    onChange={onChangeCustom}
                                />
                            </div>
                        )}
                        {/* Validation message */}
                        {error && mode === 'custom' && <div style={{ color:'red', fontSize:12, marginBottom:4 }}>{error}</div>}
                        {!hasGateway && <div style={{ fontSize:12, color:'#a94442' }}>No credit card merchant configured.</div>}
                        {hasGateway && !hasValidCard && <div style={{ fontSize:12, color:'#a94442' }}>Please select a valid credit card.</div>}
                        {/* Pay Now Button */}
                        <div className="col-md-12">
                            <button
                                type="button"
                                id="storage_payment_pay_now"
                                className="btn btn-primary w-100"
                                disabled={!canSubmit}
                                onClick={submit}
                                style={{ fontSize:25, fontWeight:'bold', height:55, background:'#092d3d', margin:'25px 0' }}
                            >
                                {submitting ? 'Processing...' : 'Pay Now'}
                            </button>
                        </div>
                    </div>
                </Modal.Body>
            </Modal>
        </PayNowErrorBoundary>
    );
};

export default StoragePayNowModal;
